import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export type EmploymentType =
  | 'fte' | 'contractor' | 'part_time' | 'intern'
  | 'seishain' | 'keiyaku' | 'gyoumu_itaku';

export type EmployeeStatus = 'pending' | 'active' | 'on_leave' | 'terminated';

export interface Employee {
  id: string;
  userId: string | null;
  legalNameLocal: string;
  legalNameKana: string | null;
  legalNameRoman: string | null;
  preferredName: string | null;
  email: string;
  countryCode: string;
  employmentType: EmploymentType;
  hireDate: string;
  probationEndDate: string | null;
  terminationDate: string | null;
  terminationReason: string | null;
  dateOfBirth: string | null;
  jobTitle: string | null;
  jobLevel: string | null;
  managerId: string | null;
  departmentId: string | null;
  workLocation: string | null;
  remoteStatus: 'onsite' | 'hybrid' | 'remote' | null;
  baseSalaryCents: number | null;
  salaryCurrency: string | null;
  salaryPeriod: 'monthly' | 'annual' | 'hourly';
  status: EmployeeStatus;
  myNumberLast4: string | null;
  myNumberUpdatedAt: string | null;
  ssnLast4: string | null;
  ukNiLast4: string | null;
  bankAccountLast4: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * True when no payroll/document/tax/performance/report row
   * references this employee — i.e., a hard delete is safe. False
   * means the UI should only offer Terminate (preserves record for
   * legal/tax retention).
   */
  canHardDelete: boolean;
}

export interface DeleteBlockReasons {
  has_payroll: boolean;
  has_documents: boolean;
  has_nencho: boolean;
  has_performance: boolean;
  has_reports: boolean;
  can_hard_delete: boolean;
}

export interface CreateEmployeeInput {
  legalNameLocal: string;
  legalNameKana?: string;
  legalNameRoman?: string;
  preferredName?: string;
  email: string;
  countryCode: string;
  employmentType: EmploymentType;
  hireDate: string;
  dateOfBirth?: string;
  jobTitle?: string;
  jobLevel?: string;
  baseSalaryCents?: number;
  salaryCurrency?: string;
  salaryPeriod?: 'monthly' | 'annual' | 'hourly';
  workLocation?: string;
  remoteStatus?: 'onsite' | 'hybrid' | 'remote';
  departmentId?: string;
  managerId?: string;
}

export interface UpdateEmployeeInput {
  preferredName?: string;
  email?: string;
  jobTitle?: string;
  jobLevel?: string;
  dateOfBirth?: string;
  departmentId?: string;
  managerId?: string;
  workLocation?: string;
  remoteStatus?: 'onsite' | 'hybrid' | 'remote';
  baseSalaryCents?: number;
  salaryCurrency?: string;
  salaryPeriod?: 'monthly' | 'annual' | 'hourly';
  status?: EmployeeStatus;
  terminationDate?: string;
  terminationReason?: string;
}

export interface BulkImportResult {
  created: Employee[];
  skipped: Array<{ row: number; reason: string }>;
}

export function useBulkImportEmployees() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (csv: string) => {
      const r = await api.post<BulkImportResult>('/api/employees/bulk-import', { csv });
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employees'] }),
  });
}

export function useEmployees(params: { status?: string; country?: string; search?: string } = {}) {
  return useQuery({
    queryKey: ['employees', params],
    queryFn: async () => {
      const r = await api.get<{ employees: Employee[] }>('/api/employees', { params });
      return r.data.employees;
    },
  });
}

/**
 * Fuzzy/semantic employee search. Hits the backend semantic endpoint, which
 * uses Qdrant vector search when configured and falls back to lexical
 * otherwise — so it always returns results. `mode` says which path ran.
 */
export function useEmployeeSearch(q: string) {
  return useQuery({
    queryKey: ['employees', 'semantic', q],
    enabled: q.trim().length > 0,
    queryFn: async () => {
      const r = await api.get<{ mode: 'semantic' | 'lexical'; employees: Employee[] }>(
        '/api/employees/search/semantic', { params: { q } },
      );
      return r.data;
    },
  });
}

export function useEmployee(id: string | undefined) {
  return useQuery({
    queryKey: ['employee', id],
    queryFn: async () => {
      if (!id) return null;
      const r = await api.get<Employee>(`/api/employees/${id}`);
      return r.data;
    },
    enabled: !!id,
  });
}

export function useEmployeeSummary() {
  return useQuery({
    queryKey: ['employees', 'summary'],
    queryFn: async () => {
      const r = await api.get<{ headcount: number; byCountry: Array<{ country_code: string; count: number }> }>('/api/employees/summary');
      return r.data;
    },
  });
}

export function useCreateEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateEmployeeInput) => {
      const r = await api.post<Employee>('/api/employees', input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employees'] }),
  });
}

export function useUpdateEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...rest }: UpdateEmployeeInput & { id: string }) => {
      const r = await api.patch<Employee>(`/api/employees/${id}`, rest);
      return r.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['employees'] });
      qc.invalidateQueries({ queryKey: ['employee', vars.id] });
    },
  });
}

export function useTerminateEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, terminationDate, reason }: { id: string; terminationDate: string; reason?: string }) => {
      const r = await api.post<Employee>(`/api/employees/${id}/terminate`, { terminationDate, reason });
      return r.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['employees'] });
      qc.invalidateQueries({ queryKey: ['employee', vars.id] });
    },
  });
}

/**
 * Hard delete. Throws 409 with `error.response.data.reasons` when the
 * employee has compliance history — callers must surface that as
 * "Terminate instead". 403 if the user isn't owner/admin.
 */
export function useDeleteEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/employees/${id}`);
      return { id };
    },
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ['employees'] });
      qc.removeQueries({ queryKey: ['employee', id] });
    },
  });
}

// ─── self-service (申請) + account linking ──────────────────
export interface ChangeRequest {
  id: string;
  employeeId: string;
  requestedBy: string | null;
  changes: Record<string, unknown>;
  note: string | null;
  status: 'pending' | 'approved' | 'rejected';
  reviewedBy: string | null;
  reviewedAt: string | null;
  createdAt: string;
}

/** The employee record linked to the logged-in account (or null). */
export function useMyEmployee() {
  return useQuery({
    queryKey: ['employee', 'me'],
    queryFn: async () => {
      const r = await api.get<Employee | null>('/api/employees/me');
      return r.data;
    },
  });
}

export function useLinkEmployeeAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; userId: string | null }) => {
      const r = await api.patch<Employee>(`/api/employees/${args.id}/link`, { userId: args.userId });
      return r.data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['employees'] }); qc.invalidateQueries({ queryKey: ['employee'] }); },
  });
}

export function useSubmitChangeRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { changes: Record<string, unknown>; note?: string }) => {
      const r = await api.post<ChangeRequest>('/api/employees/me/change-request', args);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['change-requests'] }),
  });
}

export function useChangeRequests(status = 'pending') {
  return useQuery({
    queryKey: ['change-requests', status],
    queryFn: async () => {
      const r = await api.get<ChangeRequest[]>('/api/employees/change-requests', { params: { status } });
      return r.data;
    },
  });
}

export function useDecideChangeRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; decision: 'approved' | 'rejected' }) => {
      const r = await api.post<ChangeRequest>(`/api/employees/change-requests/${args.id}/decision`, { decision: args.decision });
      return r.data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['change-requests'] }); qc.invalidateQueries({ queryKey: ['employees'] }); },
  });
}
